import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { corporateTheme, corporateLabels } from '../themes/corporate';

const FeaturesContainer = styled.div`
  width: 100%;
  padding: 6rem 2rem;
  background: ${corporateTheme.backgroundGradient};
  position: relative;
  overflow: hidden;
  font-family: ${corporateTheme.fontFamily};
  
  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 3rem 1rem;
  }
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(circle at 25% 25%, rgba(30, 64, 175, 0.1) 0%, transparent 25%),
    radial-gradient(circle at 75% 75%, rgba(37, 99, 235, 0.1) 0%, transparent 25%);
  z-index: 1;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  color: ${corporateTheme.textPrimary};
  text-align: center;
  margin-bottom: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: ${corporateTheme.textSecondary};
  text-align: center;
  max-width: 600px;
  margin: 0 auto 4rem;
  line-height: 1.6;
`;

const FeaturesGrid = styled.div`
  display: grid;
  /* 6 cards: 3x2 layout for better balance */
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-bottom: 4rem;
  
  @media (max-width: 1200px) {
    /* Switch to 2 columns for medium screens */
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin-bottom: 3rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const FeatureCard = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 2.5rem;
  border: 1px solid ${corporateTheme.cardBorder};
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: ${corporateTheme.cardShadow};
  
  &:hover {
    background: rgba(30, 64, 175, 0.05);
    border-color: rgba(30, 64, 175, 0.2);
    transform: translateY(-5px);
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const FeatureIcon = styled.div`
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  background: ${corporateTheme.primaryGradient};
  color: ${corporateTheme.background};
  padding: 1rem;
  border-radius: 50px;
  display: inline-block;
  font-weight: 600;
  letter-spacing: 1px;
  min-width: 120px;
`;

const FeatureTitle = styled.h3`
  font-size: 1.3rem;
  color: ${corporateTheme.primary};
  margin-bottom: 1rem;
  font-weight: 600;
`;

const FeatureDescription = styled.p`
  color: ${corporateTheme.textSecondary};
  line-height: 1.6;
  font-size: 1rem;
`;

const CenterFeature = styled(motion.div)`
  background: linear-gradient(135deg, rgba(30, 64, 175, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%);
  backdrop-filter: blur(20px);
  border-radius: 30px;
  padding: 4rem;
  text-align: center;
  border: 1px solid rgba(30, 64, 175, 0.2);
  position: relative;
  overflow: hidden;
  margin-bottom: 4rem;
  box-shadow: ${corporateTheme.cardShadow};
  
  &:before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: conic-gradient(from 0deg, transparent, rgba(30, 64, 175, 0.1), transparent);
    animation: rotate 10s linear infinite;
  }
  
  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  @media (max-width: 768px) {
    padding: 3rem;
    border-radius: 25px;
    margin-bottom: 3rem;
  }
  
  @media (max-width: 480px) {
    padding: 2rem;
    border-radius: 20px;
    margin-bottom: 2rem;
  }
`;

const CenterFeatureContent = styled.div`
  position: relative;
  z-index: 2;
`;

const CenterFeatureIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  background: ${corporateTheme.primaryGradient};
  color: ${corporateTheme.background};
  padding: 1.5rem;
  border-radius: 50px;
  display: inline-block;
  font-weight: 600;
  letter-spacing: 1px;
  min-width: 150px;
`;

const CenterFeatureTitle = styled.h3`
  font-size: 2.5rem;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 1rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const CenterFeatureDescription = styled.p`
  color: ${corporateTheme.textSecondary};
  font-size: 1.2rem;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
`;

const ToolsSection = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  border-radius: 25px;
  padding: 3rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
  
  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 15px;
  }
`;

const ToolsTitle = styled.h3`
  font-size: 2rem;
  color: ${corporateTheme.textPrimary};
  text-align: center;
  margin-bottom: 2rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const ToolsGrid = styled.div`
  display: grid;
  /* 8 tools: 4x2 layout for better balance */
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 1200px) {
    /* Switch to 2 columns for medium screens */
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.8rem;
  }
`;

const ToolItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(30, 64, 175, 0.05);
  border-radius: 10px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(30, 64, 175, 0.1);
    transform: translateX(5px);
  }
`;

const ToolIcon = styled.div`
  font-size: 1rem;
  color: ${corporateTheme.primary};
  background: ${corporateTheme.primaryGradient};
  color: ${corporateTheme.background};
  padding: 0.8rem;
  border-radius: 20px;
  font-weight: 600;
  min-width: 80px;
  text-align: center;
  letter-spacing: 0.5px;
`;

const ToolText = styled.div`
  color: ${corporateTheme.textSecondary};
  font-weight: 500;
`;

const FeaturesSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const features = [
    {
      icon: "ANALYTICS",
      title: "Real-Time Analytics",
      description: "Track your performance with detailed real-time reporting. See clicks, conversions, and earnings as they happen."
    },
    {
      icon: "PAYMENTS",
      title: "Weekly Payments",
      description: "Get paid every week with multiple payment options including bank transfer, crypto, and e-wallets."
    },
    {
      icon: "MARKETING",
      title: "Marketing Materials",
      description: "Access professional banners, landing pages, and promotional content designed to convert."
    },
    {
      icon: "MOBILE",
      title: "Mobile-Optimized Tools",
      description: "Manage your affiliate business on-the-go with our fully responsive dashboard and tracking tools."
    },
    {
      icon: "TARGETING",
      title: "Advanced Targeting",
      description: "Use smart targeting tools to reach the right audience and maximize your conversion rates."
    },
    {
      icon: "TRACKING",
      title: "Custom Tracking Links",
      description: "Generate personalized tracking links with advanced parameters for precise campaign tracking."
    }
  ];

  const tools = [
    { icon: "DASHBOARD", text: "Performance Dashboard" },
    { icon: "BANNER", text: "Banner Generator" },
    { icon: "CONTENT", text: "Content Templates" },
    { icon: "TESTING", text: "A/B Testing Tools" },
    { icon: "SUPPORT", text: "Live Chat Support" },
    { icon: "EMAIL", text: "Email Marketing Kit" },
    { icon: "COMMERCE", text: "E-commerce Integration Tools" },
    { icon: "MOBILE", text: "Mobile App Widgets" }
  ];

  return (
    <FeaturesContainer ref={ref} id="features">
      <BackgroundPattern />
      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <Title variants={itemVariants}>
            {corporateLabels.affiliate} Tools & Benefits
          </Title>
          
          <Subtitle variants={itemVariants}>
            Everything you need to succeed as a GrowTech affiliate partner
          </Subtitle>

          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>

          <CenterFeature variants={itemVariants}>
            <CenterFeatureContent>
              <CenterFeatureIcon>MANAGER</CenterFeatureIcon>
              <CenterFeatureTitle>Dedicated Account Manager</CenterFeatureTitle>
              <CenterFeatureDescription>
                Every affiliate gets a personal account manager who understands your business 
                and helps optimize your campaigns for maximum revenue. Get personalized strategies, 
                exclusive offers, and priority support.
              </CenterFeatureDescription>
            </CenterFeatureContent>
          </CenterFeature>

          <ToolsSection variants={itemVariants}>
            <ToolsTitle>Your {corporateLabels.affiliate} Toolkit</ToolsTitle>
            <ToolsGrid>
              {tools.map((tool, index) => (
                <ToolItem
                  key={index}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <ToolIcon>{tool.icon}</ToolIcon>
                  <ToolText>{tool.text}</ToolText>
                </ToolItem>
              ))}
            </ToolsGrid>
          </ToolsSection>
        </motion.div>
      </Container>
    </FeaturesContainer>
  );
};

export default FeaturesSection; 